import { makeImmutable, Money, Tristate, } from "../../core";
/**
 * A price of an item.
 *
 * Price can be parametric - when not all parameters are resolved it is represented
 * as a range (min/max) of possible values.
 * Negative prices are intepreted as discounts
 */
export class Price {
    constructor(spectrum) {
        this._spectrum = spectrum;
        makeImmutable(this);
    }
    combineWith(other, fn) {
        const a = this._spectrum;
        const b = other._spectrum;
        if (a.type === "null" || b.type === "null") {
            return new Price({ type: "null" });
        }
        if (a.type === "value" && b.type === "value") {
            return new Price({ type: "value", value: fn(a.value, b.value) });
        }
        const aValues = a.type === "value" ? [a.value] : [a.min, a.max];
        const bValues = b.type === "value" ? [b.value] : [b.min, b.max];
        const results = [];
        for (const av of aValues) {
            for (const bv of bValues) {
                results.push(fn(av, bv));
            }
        }
        const first = results[0];
        const min = results.reduce((m, v) => m.min(v), first);
        const max = results.reduce((m, v) => m.max(v), first);
        return new Price(min.eq(max) ? { type: "value", value: min } : { type: "range", min, max });
    }
    mapWith(fn) {
        const s = this._spectrum;
        if (s.type === "null") {
            return this;
        }
        if (s.type === "value") {
            return new Price({ type: "value", value: fn(s.value) });
        }
        const mappedMin = fn(s.min);
        const mappedMax = fn(s.max);
        const min = mappedMin.min(mappedMax);
        const max = mappedMin.max(mappedMax);
        return new Price(min.eq(max) ? { type: "value", value: min } : { type: "range", min, max });
    }
    /**
     * Add another Price
     */
    add(other) {
        return this.combineWith(other, (a, b) => a.add(b));
    }
    sub(other) {
        return this.combineWith(other, (a, b) => a.sub(b));
    }
    /**
     * Get the largest of two Prices
     */
    max(other) {
        return this.combineWith(other, (a, b) => a.max(b));
    }
    /**
     * Get the smallest of two Prices
     */
    min(other) {
        return this.combineWith(other, (a, b) => a.min(b));
    }
    roundUpTo50Cents() {
        return this.mapWith((value) => value.roundUpTo50Cents());
    }
    /**
     * Multiply by a Quantity
     */
    multiply(quantity) {
        return this.mapWith((value) => value.multiply(quantity));
    }
    percentage(percentage) {
        return this.mapWith((value) => value.percentage(percentage));
    }
    half() {
        return this.mapWith((value) => value.half());
    }
    compare(otherPrice) {
        const diff = this.sub(otherPrice)._spectrum;
        if (diff.type === "null")
            return 0;
        if (diff.type === "value")
            return Math.sign(diff.value.cents);
        if (diff.min.gt(Money.zero()))
            return 1;
        if (diff.max.lt(Money.zero()))
            return -1;
        return Math.sign((diff.min.cents + diff.max.cents) / 2);
    }
    eq(other) {
        return this.compare(other) == 0;
    }
    get negated() {
        return this.mapWith((value) => value.negated);
    }
    /**
     * Sum provided prices
     */
    static total(prices) {
        return [...prices].reduce((a, b) => a.add(b), Price.free());
    }
    static free() {
        return Price.unscoped(Money.zero());
    }
    static unscoped(value) {
        return new Price({ type: "value", value });
    }
    static scoped(pricing, parameterSet) {
        const applied = pricing.unify(parameterSet);
        const values = applied.values;
        const sample = values.sample;
        if (sample === null) {
            return new Price({ type: "null" });
        }
        if (values.size === 1) {
            return new Price({ type: "value", value: sample });
        }
        const min = values.reduce((a, b) => a.min(b), sample);
        const max = values.reduce((a, b) => a.max(b), sample);
        if (min.eq(max)) {
            return new Price({ type: "value", value: min });
        }
        return new Price({ type: "range", min, max });
    }
    /**
     * Get the spectrum of the price
     *
     * Since a parametric value (price) can never be assumed to be fully applied,
     * this is the most convinient way to get the value of the price.
     */
    get spectrum() {
        return this._spectrum;
    }
    get hasPossibleValues() {
        return this._spectrum.type !== "null";
    }
    get finalValue() {
        if (this._spectrum.type === "value") {
            return this._spectrum.value;
        }
        return null;
    }
    test(predicate) {
        switch (this._spectrum.type) {
            case "null":
                return Tristate.False;
            case "value":
                return Tristate.fromBoolean(predicate(this._spectrum.value));
            case "range": {
                const testMin = predicate(this._spectrum.min);
                const testMax = predicate(this._spectrum.max);
                if (testMin && testMax)
                    return Tristate.True;
                if (!testMin && !testMax)
                    return Tristate.False;
                return Tristate.Maybe;
            }
        }
    }
}
